<template>
	<div class="map-wrapper">
		<div class="map-container" id="map"></div>

		<!-- Desktop sidebar -->
		<div v-if="!isMobile" class="apartment-sidebar"
			v-show="sidebarContent.length">
			<button class="close-btn" @click="closeSidebar">
				<CloseIcon />
			</button>
			<ApartmentCard v-for="apartment in sidebarContent" :apartment="apartment"
				:key="apartment.id" :get-params="{ back: true }" />
		</div>

		<!-- Mobile Flickity slider -->
		<div class="apartment-slider-wrapper"
			v-if="isMobile && sidebarContent.length">
			<button class="close-btn slider-close-btn" @click="closeSidebar">
				<CloseIcon />
			</button>
			<div class="apartment-slider flickity-enabled" ref="flickitySlider">
				<div class="apartment-cell" v-for="apartment in sidebarContent"
					:key="apartment.id">
					<ApartmentCard :apartment="apartment" />
				</div>
			</div>
		</div>

	</div>
</template>

<script>
import L, { latLng } from "leaflet";

// Import Leaflet markerCluster files.
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';

import Flickity from 'flickity';

// Apartment card component.
import ApartmentCard from './ApartmentCard';

// Icons.
import CloseIcon from '../icons/CloseIcon.vue';

export default {
	name: 'ApartmentSearchMap',
	components: {
		Flickity,
		ApartmentCard,
		CloseIcon,
	},
	props: {
		apartments: Array, // Array of apartment objects with name & coordinates
		required: true,
	},
	data() {
		return {
			map: null,
			sidebarContent: [], // one or multiple apartments
			activeMarker: null,
			flickityInstance: null,
			windowWidth: window.innerWidth,
		};
	},
	computed: {
		simplifiedApartments() {
			return this.apartments;
		},
		isMobile() {
			return this.windowWidth <= 768;
		}
	},
	watch: {
		simplifiedApartments: {
			handler(newVal) {
				if (newVal.length > 0) {
					this.initMap(newVal);
				}
			},
			immediate: true
		},
		sidebarContent(newVal) {
			if (this.isMobile && newVal.length) {
				this.$nextTick(() => {
					if (this.flickityInstance) {
						this.flickityInstance.destroy();
					}
					this.flickityInstance = new Flickity(this.$refs.flickitySlider, {
						initialIndex: 0,
						pageDots: false,
						prevNextButtons: false,
						wrapAround: false
					});

					// 🔸 Add class to <body> to lock scroll or indicate state
					document.body.classList.add('flickity-active');

					// Position the close button initially
					this.positionCloseButton();

					// Optional: When slide changes, update map marker
					this.flickityInstance.on('change', index => {
						const apartment = this.sidebarContent[index];
						const marker = this.apartmentMarkers.find(m => m.apartmentId === apartment.id);
						this.setActiveMarker(marker);
						const latitude = parseFloat(apartment.housing_locality.ava_xkoordinaatti);
						const longitude = parseFloat(apartment.housing_locality.ava_ykoordinaatti);
						this.map.setView([latitude, longitude], this.map.getZoom());
					});
				});
			}
		}
	},
	mounted() {
		window.addEventListener('resize', this.onResize);
	},
	beforeUnmount() {
		window.removeEventListener('resize', this.onResize);
	},
	methods: {
		initMap(apartments) {

			// Wrap the map initialization inside this.$nextTick() so that Vue ensures the DOM is fully rendered before Leaflet tries to grab the #map div.
			this.$nextTick(async () => {

				if (this.map) {
					this.map.remove(); // Recreate if already initialized
				}

				this.map = L.map('map', {
					attributionControl: false,
					zoomControl: false // disable default zoom control
				});

				const map = this.map;

				// Add zoom control to the map.
				L.control.zoom({ position: 'topright' }).addTo(this.map);

				let initialCenter = [];
				let initialZoom = null;

				L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
					attribution: '&copy; OpenStreetMap contributors',
				}).addTo(map);

				// Create the cluster group.
				const markers = L.markerClusterGroup({
					spiderfyOnMaxZoom: false, // Enable spiderfying on max zoom
					spiderfyDistanceMultiplier: 4, // Set how far apart markers should spread
					zoomAnimation: true, // Enable zoom animation when spiderfying
					showCoverageOnHover: false, // Disable coverage (bounds of its markers) on hover
					//disableClusteringAtZoom: 16, // Disable clustering at zoom level 15
					spiderLegPolylineOptions: { weight: 4, color: '#222', opacity: 0.5 },
					iconCreateFunction: function (cluster) {
						const childCount = cluster.getChildCount();
						const isMaxZoom = map.getZoom() >= map.getMaxZoom();

						let clusterIconSizeClass = ' marker-cluster-';
						if (childCount < 10) {
							clusterIconSizeClass += 'small';
						} else if (childCount < 20) {
							clusterIconSizeClass += 'medium';
						} else {
							clusterIconSizeClass += 'large';
						}

						// If at max zoom, show a special map-pin-like cluster icon with count.
						if (isMaxZoom) {
							return L.divIcon({
								html:
									`<div class="avain-apartment-icon avain-apartment-cluster-icon">
										<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#004722" stroke="#fff" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-map-pin-icon lucide-map-pin">
											<path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/>
										</svg>
										<span>${childCount}</span>
									</div>`,
								className: 'leaflet-marker-icon',
								iconSize: [54, 54],
								iconAnchor: [27, 27],
								popupAnchor: [0, -27]
							});
						} else {
							// For clusters at lower zoom levels, use a simple count icon.
							return L.divIcon({
								html: `<div class="avain-cluster-icon"><span>${childCount}</span></div>`,
								className: 'leaflet-cluster ' + clusterIconSizeClass, // Keep 'leaflet-cluster' for default transitions
								iconAnchor: [36, 36] // Center the spiderfied marker icons
							});
						}
					}
				});

				this.apartmentMarkers = []; // Store for later

				// Loop through apartments and create markers.
				const geocoded = {};
				for (const apartment of apartments) {

					// Define custom SVG icon for map pin.
					const svgIcon = L.divIcon({
						html: `<div class="avain-apartment-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#004722" stroke="#fff" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-map-pin-icon lucide-map-pin"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3"/></svg></div>`,
						className: 'leaflet-marker-icon',
						iconSize: [48, 48],
						iconAnchor: [24, 24],
						popupAnchor: [0, -24]
					});

					var latitude = parseFloat(apartment.housing_locality.ava_xkoordinaatti);
					var longitude = parseFloat(apartment.housing_locality.ava_ykoordinaatti);

					// If coordinates are invalid or non-existent, try to geocode the address.
					if (isNaN(latitude) || isNaN(longitude)) {
						console.warn(`Invalid coordinates for apartment ${apartment.id}:`, latitude, longitude);
						if (!(apartment.housing_locality.mpc_katuosoite + " " + apartment.city.mpc_name in geocoded)) {
							// Get the Maptiler API key from environment variables in .env file.
							const apiKey = process.env.MIX_MAPTILER_API_KEY;
							const response = await fetch(`https://api.maptiler.com/geocoding/${apartment.housing_locality.mpc_katuosoite},${apartment.city.mpc_name},suomi.json?key=${apiKey}`);
							const data = await response.json();
							const geocodedCoords = latLng(data.features[0].center[1], data.features[0].center[0]);
							geocoded[apartment.housing_locality.mpc_katuosoite + " " + apartment.city.mpc_name] = geocodedCoords;
						}
						latitude = geocoded[apartment.housing_locality.mpc_katuosoite + " " + apartment.city.mpc_name].lat;
						longitude = geocoded[apartment.housing_locality.mpc_katuosoite + " " + apartment.city.mpc_name].lng;
					}

					const marker = L.marker([latitude, longitude], { icon: svgIcon });
					marker.apartmentData = apartment;
					marker.apartmentId = apartment.id;

					// Save reference
					this.apartmentMarkers.push(marker);

					// Add event listeners to change the marker class/color on popup open/close.
					marker.on('popupopen', () => {
						const el = marker.getElement();
						if (el) el.classList.add('active');
					});

					marker.on('popupclose', () => {
						const el = marker.getElement();
						if (el) el.classList.remove('active');
					});

					// For individual marker
					marker.on('click', () => {
						this.showSidebar(apartment); // show one apartment in sidebar
					});

					markers.addLayer(marker);
				}

				// Add the markers to the map.
				map.addLayer(markers);

				markers.on('clusterclick', e => {
					// For cluster at max zoom, show sidebar with cluster apartments.
					const isMaxZoom = map.getZoom() >= map.getMaxZoom();
					if (isMaxZoom) {
						const apartmentsInCluster = e.layer
							.getAllChildMarkers()
							.map(m => m.apartmentData)
							.slice() // make a shallow copy
							.sort((a, b) => a.mpc_name.localeCompare(b.mpc_name)); // sort by name
						this.showSidebar(apartmentsInCluster);
					} else {
						// Zoom to bounds of the cluster when clicked
						map.fitBounds(e.layer.getBounds(), { padding: [50, 50] });
					}
				});

				// Fit map bounds initially to include all markers.
				const validCoordinates = apartments
					.map(a => [
						parseFloat(a.housing_locality.ava_xkoordinaatti),
						parseFloat(a.housing_locality.ava_ykoordinaatti)
					])
					.filter(([lat, lng]) => !isNaN(lat) && !isNaN(lng));

				if (validCoordinates.length > 0) {
					const bounds = L.latLngBounds(validCoordinates);
					map.fitBounds(bounds, { padding: [25, 25] });
				}

				// Retrieve the initial computed center and zoom level.
				initialCenter = map.getCenter();
				initialZoom = map.getZoom();

				// Add a reset view control to the map.
				const resetViewControl = L.Control.extend({
					options: {
						position: 'topright' // or 'topleft', 'bottomleft', etc.
					},

					onAdd: function (map) {
						const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-control-custom');
						const button = L.DomUtil.create('a', 'leaflet-control-reset-button', container);

						button.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-rotate-ccw-icon lucide-rotate-ccw"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>';
						button.title = 'Reset map view';

						// Prevent map from panning when clicking
						L.DomEvent.disableClickPropagation(container);

						container.onclick = function () {
							map.setView(initialCenter, initialZoom);
						};

						return container;
					}
				});

				// Add to map
				map.addControl(new resetViewControl());

			});
		},
		showSidebar(apartments) {
			this.sidebarContent = Array.isArray(apartments) ? apartments : [apartments];

			// Highlight first marker if showing one apartment
			if (this.sidebarContent.length === 1) {
				const apartment = this.sidebarContent[0];

				// Find the corresponding marker
				const marker = this.apartmentMarkers.find(m => m.apartmentId === apartment.id);
				this.setActiveMarker(marker);
			} else {
				// Multiple apartments = cluster, deactivate current
				this.setActiveMarker(null);
			}
		},
		closeSidebar() {
			this.sidebarContent = [];
			this.destroyFlickity();
			this.setActiveMarker(null);
		},
		setActiveMarker(marker) {
			if (this.activeMarker) {
				this.activeMarker.getElement()?.classList.remove('active');
			}

			if (marker) {
				marker.getElement()?.classList.add('active');
			}

			this.activeMarker = marker;
		},
		onResize() {
			this.windowWidth = window.innerWidth;
		},
		destroyFlickity() {
			if (this.flickityInstance) {
				this.flickityInstance.destroy();
				this.flickityInstance = null;
			}

			// Remove scroll lock and any Flickity-specific body class
			document.body.classList.remove('flickity-active');
		},
		positionCloseButton() {
			const cell = this.flickityInstance?.selectedElement;
			const closeBtn = document.querySelector('.slider-close-btn');

			if (cell && closeBtn) {
				const rect = cell.getBoundingClientRect();
				closeBtn.style.top = `${rect.top - 38}px`;
				closeBtn.style.left = `${rect.right - closeBtn.offsetWidth}px`;
			}
		}
	}
};
</script>

<style lang="scss">
@import "~styles/definitions";

$mobile-slider-cell-max-width: 20rem;
$mobile-slider-scaling: 0.9;

.map-container {
	width: 100%;
	height: 75lvh;
	max-height: 50rem;
	z-index: 90; // Prevent overlap with navigation
	margin-bottom: r(40);
}

.apartment-sidebar {
	position: absolute;
	top: 0;
	left: 0;
	width: 25rem;
	height: 100%;
	background: rgba(0, 0, 0, 0.5);
	overflow-y: auto;
	overflow-x: hidden;
	padding: 1rem;
	z-index: 90;
}

.apartment-sidebar .base-card,
.apartment-cell .base-card {
	transform-origin: top left;
	box-shadow: 0px 4px 4px 0px #00000040;
}

.apartment-sidebar .base-card {
	transform: scale(0.8);
	margin-bottom: -5rem;
	width: 125%;
}

.apartment-cell .base-card {
	transform: scale($mobile-slider-scaling);
	width: calc(100% / $mobile-slider-scaling);
}

.apartment-cell .base-card__hover {
	font-size: 1.33rem;
}

.apartment-sidebar .base-card__content,
.apartment-cell .base-card__content {
	padding: 1rem 1.25rem;
}

.apartment-cell .apartment-card__next-showing {
	position: absolute;
	bottom: 0;
	width: 100%;
}

.apartment-cell .base-card__specs>.base-card__spec:first-of-type {
	text-overflow: ellipsis;
	white-space: nowrap;
	overflow: hidden;
}

.apartment-cell .base-card__specs>.base-card__spec:nth-child(n+2) {
	flex-shrink: 1;
	white-space: nowrap;
}

.close-btn {
	background-color: transparent;
	border: none;
	cursor: pointer;
	position: sticky;
	top: 0rem;
	z-index: 1;
	width: 1rem;
	height: 1rem;
	padding: 1.5rem;
	display: grid;
	place-items: center;
	margin-left: auto;
	margin-top: -.5rem;
	transform: translateX(.75rem) translateY(-.75rem);
}

.close-btn .icon {
	position: absolute;
	background-color: $color-pink-2025;
	border-radius: 1rem;
}

.slider-close-btn {
	position: absolute;
	z-index: 10;
	cursor: pointer;
}

.leaflet-cluster {
	padding: .66rem;
}

.avain-cluster-icon {
	background-color: $color-green-2025;
	font-family: $ff-lato;
	color: $color-white;
	border-radius: 50%;
	width: 48px;
	height: 48px;
	display: flex;
	align-items: center;
	justify-content: center;
	font-weight: 500;
	font-size: 1.2rem;
	transition: transform 0.2s ease;
	border: 2px solid $color-white;
}

.leaflet-cluster:hover .avain-cluster-icon {
	transform: scale(1.1);
}

.avain-apartment-icon {
	width: 54px;
	height: 54px;
	transition: transform 0.2s ease;
}

.avain-apartment-icon:hover {
	transform: scale(1.1);
}

.avain-apartment-cluster-icon {
	position: relative;
}

.avain-apartment-cluster-icon span {
	color: $color-white;
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -60%);
	font-family: $ff-lato;
	font-size: 1.2rem;
	font-weight: 500;
}

.leaflet-marker-icon.active svg {
	fill: $color-pink-2025;
	stroke: $color-green-2025;
}

.leaflet-control-reset-button {
	display: grid;
	place-items: center;
	cursor: pointer;
	padding: .25rem
}

.map-wrapper {
	position: relative;
	height: 100%;
}

.apartment-slider-wrapper {
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background: rgba(0, 0, 0, 0.5);
	z-index: 100;
}

.apartment-slider {
	position: relative;
	top: 50%;
	transform: translateY(calc(-50% * $mobile-slider-scaling)); // Center vertically, take scaling into account
}

.apartment-cell {
	max-width: $mobile-slider-cell-max-width;
}

.apartment-cell+.apartment-cell {
	margin-left: r(12);
}

.apartment-cell .apartment-popup {
	min-width: unset;
}

.flickity-viewport {
	overflow: visible;
}

body.flickity-active {
	overflow: hidden;
	touch-action: none;
}
</style>
